import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { getAllFeedbackService } from "@/service/feedback.service";
import { FeedbackModel, FeedbackQuery } from "@/types/feedback.model";
import type { AxiosError } from "axios";

interface FeedbackState {
  feedbacks: FeedbackModel[];
  currentFeedback: FeedbackModel | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  totalItems: number;
  noFeedbacks: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: FeedbackState = {
  feedbacks: [],
  currentFeedback: null,
  loading: false,
  actionLoading: false,
  error: null,
  totalItems: 0,
  noFeedbacks: true,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Async Actions
export const fetchFeedbacks = createAsyncThunk(
  "feedback/fetchAll",
  async (query: FeedbackQuery, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const {
        data: { data, pageInfo },
      } = await getAllFeedbackService(query);
      dispatch(setLoading(false));

      dispatch(setNoFeedbacks(false));

      return {
        feedbacks: data,
        // Handle different API response formats for pagination
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoFeedbacks(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch feedbacks";
      dispatch(setError(message));
      throw error;
    }
  }
);

const feedbackSlice = createSlice({
  name: "feedback",
  initialState,
  reducers: {
    setNoFeedbacks: (state, action: PayloadAction<boolean>) => {
      state.noFeedbacks = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.actionLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCurrentFeedback: (state) => {
      state.currentFeedback = null;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all feedbacks
      .addCase(fetchFeedbacks.fulfilled, (state, action) => {
        state.feedbacks = action.payload.feedbacks;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch feedbacks";
      });
  },
});

export const {
  setNoFeedbacks,
  setLoading,
  setActionLoading,
  setError,
  clearError,
  resetCurrentFeedback,
  resetAllState,
} = feedbackSlice.actions;

// Selectors
export const selectFeedbacks = (state: RootState) => state.feedbacks.feedbacks;
export const selectCurrentFeedback = (state: RootState) =>
  state.feedbacks.currentFeedback;
export const selectFeedbackLoading = (state: RootState) =>
  state.feedbacks.loading;
export const selectFeedbackActionLoading = (state: RootState) =>
  state.feedbacks.actionLoading;
export const selectFeedbackError = (state: RootState) => state.feedbacks.error;
export const selectTotalItems = (state: RootState) =>
  state.feedbacks.pagination.totalItems;
export const selectNoFeedbacks = (state: RootState) =>
  state.feedbacks.noFeedbacks;

export default feedbackSlice.reducer;
